/**
 * Register the Distill GS service worker.
 *
 * Design notes:
 *   • We only register in production builds. During `yarn start`, CRA's
 *     dev server + a live SW will fight each other and confuse HMR.
 *   • We fail *silently* — a broken SW must never take the app down.
 *   • On a new version being detected, we auto-activate (skipWaiting) so
 *     returning users are always on the freshest build for something as
 *     time-sensitive as a news app.
 */
export function registerServiceWorker() {
  if (typeof window === 'undefined') return;
  if (!('serviceWorker' in navigator)) return;

  // Only register on production origins. In development, actively unregister
  // any lingering SW so DevTools cache doesn't confuse the developer.
  const isProd = process.env.NODE_ENV === 'production';
  if (!isProd) {
    navigator.serviceWorker.getRegistrations().then((regs) => {
      regs.forEach((r) => r.unregister().catch(() => null));
    }).catch(() => null);
    return;
  }

  // Wait for the page load event so the SW never competes with the initial
  // paint of the app — critical on slow mobile networks.
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/service-worker.js', { scope: '/' })
      .then((registration) => {
        // Listen for a new SW being installed while the app is open.
        registration.addEventListener('updatefound', () => {
          const installing = registration.installing;
          if (!installing) return;
          installing.addEventListener('statechange', () => {
            if (
              installing.state === 'installed' &&
              navigator.serviceWorker.controller
            ) {
              // A new SW is waiting — activate it right away for news freshness.
              installing.postMessage({ type: 'SKIP_WAITING' });
            }
          });
        });
      })
      .catch(() => {
        // Swallow silently — a broken SW must not kill the app.
      });

    // Reload once the newly-activated SW takes control, so the user sees the
    // freshest build without needing to pull-to-refresh manually.
    let refreshing = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (refreshing) return;
      refreshing = true;
      window.location.reload();
    });
  });
}
